import axios from "axios"
import { getApiType } from "@/utils/apiType"
import { captureForumSession, isCurrentForumSession, clearForumSession, staleForumSessionError } from './forumSession.js'
import { assertMeetingSession } from './meetingSessionGuard.js'
import { redirectToMeetingLogin } from './meetingLoginRedirect.js'

const service = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  timeout: 10000,
})

function getUserServiceUrl(url = "") {
  if (getApiType() !== "1") {
    return url
  }

  return url.startsWith("/meritco-user/")
    ? url.replace("/meritco-user/", "/matrix-search/")
    : url
}

function getUserServiceBaseURL() {
  const researchUrl = import.meta.env.VITE_API_RESEARCH_URL || "/api"
  const forumUrl = import.meta.env.VITE_API_FORUM_URL || researchUrl

  return getApiType() === "1" ? forumUrl : researchUrl
}

const userService = axios.create({
  baseURL: getUserServiceBaseURL(),
  timeout: 10000,
})

function getAuthServiceBaseURL() {
  if (import.meta.env.DEV) {
    return ""
  }

  return import.meta.env.VITE_API_AUTH_URL || import.meta.env.VITE_API_RESEARCH_URL || "/api"
}

const authService = axios.create({
  baseURL: getAuthServiceBaseURL(),
  timeout: 10000,
})

const getCurrentLang = () => {
  return localStorage.getItem("lang") || "zh-CN"
}

function handleUnauthorized(config) {
  if (!config?._forumAuthAttached || !config._forumSession?.token || !isCurrentForumSession(config._forumSession)) return
  if (!clearForumSession(config._forumSession)) return
  redirectToMeetingLogin()
}

function createUnauthorizedError(response) {
  const error = new Error(response.data?.message || "Unauthorized")
  error.response = response
  error.isUnauthorized = true
  return error
}

service.interceptors.request.use(
  (config) => {
    config.headers["Accept-Language"] = getCurrentLang()
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

userService.interceptors.request.use(
  (config) => {
    const session = captureForumSession()
    if (config.expectedForumSession && !isCurrentForumSession(config.expectedForumSession)) {
      return Promise.reject(staleForumSessionError())
    }
    const token = session.token
    config._forumSession = !config.skipToken || config.bindSession ? session : null
    config._forumAuthAttached = Boolean(token && !config.skipToken)
    if (!config.skipToken && !config.sessionCheck) assertMeetingSession()
    config.headers = config.headers || {}
    delete config.headers.token
    if (token && !config.skipToken) {
      config.headers.token = `${token}`
    }
    delete config.skipToken
    config.url = getUserServiceUrl(config.url)
    config.headers["Accept-Language"] = getCurrentLang()
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

authService.interceptors.request.use(
  (config) => {
    config.headers["Accept-Language"] = getCurrentLang()
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

service.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    return Promise.reject(error)
  }
)

userService.interceptors.response.use(
  (response) => {
    if (response.config?._forumSession && !isCurrentForumSession(response.config._forumSession)) {
      return Promise.reject(staleForumSessionError())
    }
    if ([401, 600].includes(Number(response.data?.code))) {
      handleUnauthorized(response.config)
      return Promise.reject(createUnauthorizedError(response))
    }

    return response.data
  },
  (error) => {
    const config = error.config || error.response?.config
    if (config?._forumSession && !isCurrentForumSession(config._forumSession)) {
      return Promise.reject(staleForumSessionError())
    }
    const status = Number(error.response?.status)
    const code = Number(error.response?.data?.code)

    if (status === 401 || status === 600 || code === 600 || code === 401) {
      handleUnauthorized(config)
      return Promise.reject(createUnauthorizedError(error.response))
    }

    return Promise.reject(error)
  }
)

authService.interceptors.response.use(
  (response) => {
    return response.data
  },
  (error) => {
    return Promise.reject(error)
  }
)

function get(url, params = {}, config = {}) {
  return service.get(url, { params, ...config })
}

function post(url, data = {}, config = {}) {
  return service.post(url, data, config)
}

function userGet(url, params = {}, config = {}) {
  return userService.get(url, { params, ...withForumSession(config) })
}

function userPost(url, data = {}, config = {}) {
  return userService.post(url, data, withForumSession(config))
}

function withForumSession(config) {
  return config.skipToken && !config.bindSession ? config : {
    ...config, expectedForumSession: config.expectedForumSession || captureForumSession(),
  }
}

function authGet(url, params = {}, config = {}) {
  return authService.get(url, { params, ...config })
}

function authPost(url, data = {}, config = {}) {
  return authService.post(url, data, config)
}

export {
  service,
  userService,
  authService,
  get,
  post,
  userGet,
  userPost,
  authGet,
  authPost,
}
